import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmtINR, fmtNum, fmtDate, today, fmtINRCompact } from "@/lib/format";
import { Download, TrendingUp, Users, Package, AlertOctagon, ShieldCheck, IndianRupee, Building2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { toast } from "sonner";
import BudgetVarianceCard from "@/components/reports/BudgetVarianceCard";
import ApportionmentConfig from "@/components/mis/ApportionmentConfig";
import MisSortableTable from "@/components/mis/MisSortableTable";
import MisViewsBar from "@/components/mis/MisViewsBar";
import PivotBuilder from "@/pages/mis/PivotBuilder";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs as DTabs, TabsList as DTL, TabsTrigger as DTT, TabsContent as DTC } from "@/components/ui/tabs";
import { PieChart as PC, Pie as P2, Cell as C2 } from "recharts";

const PIE_COLORS = ["hsl(158 89% 33%)", "hsl(220 90% 55%)", "hsl(43 96% 44%)", "hsl(22 96% 47%)",
                    "hsl(280 76% 55%)", "hsl(158 60% 40%)", "hsl(340 82% 52%)", "hsl(198 93% 60%)",
                    "hsl(24 100% 50%)", "hsl(160 84% 39%)"];

const fyStart = () => { const t = new Date(); const y = t.getMonth() >= 3 ? t.getFullYear() : t.getFullYear() - 1; return `${y}-04-01`; };

function KPI({ label, value, sub, tone = "default", icon: Icon }) {
  return (
    <div className="card-sharp p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
          <div className="metric-value text-2xl mt-2">{value}</div>
          {sub && <div className={`text-xs mt-2 ${tone === "positive" ? "text-emerald-600" : tone === "negative" ? "text-red-600" : "text-muted-foreground"}`}>{sub}</div>}
        </div>
        {Icon && <div className="w-9 h-9 rounded-sm bg-accent/15 text-accent flex items-center justify-center"><Icon className="w-4 h-4" /></div>}
      </div>
    </div>
  );
}

export default function MISPack() {
  const [fromDate, setFromDate] = useState(fyStart());
  const [toDate, setToDate] = useState(today());
  const [pack, setPack] = useState(null);
  const [unusual, setUnusual] = useState({ rows: [] });
  const [loading, setLoading] = useState(false);
  const [threshold, setThreshold] = useState(500000);
  const [departments, setDepartments] = useState([]);
  const [departmentId, setDepartmentId] = useState("");
  const [drillRow, setDrillRow] = useState(null);           // Iter-109 · P&L drill-down
  const [drillData, setDrillData] = useState(null);
  const [drillBusy, setDrillBusy] = useState(false);
  const [accData, setAccData] = useState(null);             // Iter-110 · By-account tab
  const [vchData, setVchData] = useState(null);             // Iter-111 · By-voucher tab
  // Iter-172 · Personal MIS views (drag/drop + hide + save)
  const [globalRowOrder, setGlobalRowOrder] = useState([]);
  const [globalColOrder, setGlobalColOrder] = useState([]);
  const [globalHiddenRows, setGlobalHiddenRows] = useState(new Set());
  const [globalHiddenCols, setGlobalHiddenCols] = useState(new Set());
  // Iter-174 · Segment MIS drag/drop for business + geo dimensions
  const [bizFullPnl, setBizFullPnl] = useState(null);
  const [geoFullPnl, setGeoFullPnl] = useState(null);
  const [bizRowOrder, setBizRowOrder] = useState([]);
  const [bizColOrder, setBizColOrder] = useState([]);
  const [bizHiddenRows, setBizHiddenRows] = useState(new Set());
  const [bizHiddenCols, setBizHiddenCols] = useState(new Set());
  const [geoRowOrder, setGeoRowOrder] = useState([]);
  const [geoColOrder, setGeoColOrder] = useState([]);
  const [geoHiddenRows, setGeoHiddenRows] = useState(new Set());
  const [geoHiddenCols, setGeoHiddenCols] = useState(new Set());

  const openDrill = async (row) => {
    if (row.tone === "subtotal") return;
    setDrillRow(row); setDrillData(null); setAccData(null); setVchData(null); setDrillBusy(true);
    try {
      const params = { row_key: row.key, from_date: fromDate, to_date: toDate };
      const acc_params = departmentId ? { ...params, department_id: departmentId } : params;
      const [d, a, v] = await Promise.all([
        api.get("/reports/mis/row-by-department", { params }),
        api.get("/reports/mis/row-by-account", { params: acc_params }),
        api.get("/reports/mis/row-by-voucher", { params: acc_params }),
      ]);
      setDrillData(d.data); setAccData(a.data); setVchData(v.data);
    } catch (e) { toast.error("Drill-down failed"); }
    setDrillBusy(false);
  };

  useEffect(() => {
    api.get("/departments").then((r) => setDepartments(r.data || [])).catch(() => setDepartments([]));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const dept = departmentId ? { department_id: departmentId } : {};
      const p = { from_date: fromDate, to_date: toDate, ...dept };
      const [glob, biz, geo, prods, custs, comp, ar, ap, unus] = await Promise.all([
        api.get("/reports/mis/global-pnl"),
        api.get("/reports/mis/full-pnl", { params: { ...p, dimension: "business" } }),
        api.get("/reports/mis/full-pnl", { params: { ...p, dimension: "geo" } }),
        api.get("/reports/mis/top-products", { params: p }),
        api.get("/reports/mis/top-customers", { params: p }),
        api.get("/reports/mis/compliance", { params: { as_of: toDate } }),
        api.get("/reports/aging", { params: { party_type: "customer" } }),
        api.get("/reports/aging", { params: { party_type: "vendor" } }),
        api.get("/reports/mis/unusual-transactions", { params: { ...p, threshold } }),
      ]);
      setPack({ global: glob.data, biz: biz.data, geo: geo.data, prods: prods.data,
                custs: custs.data, comp: comp.data, ar: ar.data, ap: ap.data });
      setBizFullPnl(biz.data);
      setGeoFullPnl(geo.data);
      setUnusual(unus.data);
    } catch (e) { toast.error("Failed to load MIS pack"); console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const downloadXlsx = async () => {
    try {
      const params = { from_date: fromDate, to_date: toDate };
      if (departmentId) params.department_id = departmentId;
      const r = await api.get("/reports/mis/pack.xlsx", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `MIS_Pack_${toDate}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Excel download failed"); }
  };

  const downloadPdf = async () => {
    try {
      const params = { from_date: fromDate, to_date: toDate };
      if (departmentId) params.department_id = departmentId;
      const r = await api.get("/reports/mis/pack.pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `MIS_Pack_${toDate}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("PDF download failed"); }
  };

  if (!pack) return <div className="p-10 text-muted-foreground">{loading ? "Loading MIS pack…" : "Set period and click Refresh"}</div>;

  const globRow = (k) => pack.global.rows?.find((r) => r.key === k)?.values || {};
  const ebitdaCurr = globRow("ebitda").reporting_month || 0;
  const ebitdaYTD = globRow("ebitda").ytd || 0;
  const eatYTD = globRow("eat")?.ytd || 0;
  const revYTD = globRow("total_income")?.ytd || 0;

  // Segment EBITDA data for chart
  const segBizChart = (pack.biz.columns || []).map((c) => {
    const row = pack.biz.rows.find((r) => r.key === "ebitda");
    return { name: c.label || c.name, ebitda: row?.values?.[c.id] || 0 };
  }).filter((r) => r.name !== "Total");

  const segGeoChart = (pack.geo.columns || []).map((c) => {
    const row = pack.geo.rows.find((r) => r.key === "ebitda");
    return { name: c.label || c.name, ebitda: row?.values?.[c.id] || 0 };
  }).filter((r) => r.name !== "Total");

  const topProdChart = (pack.prods.rows || []).slice(0, 10).map((p) => ({ name: p.name, gm: p.gross_margin, revenue: p.revenue }));
  const topCustChart = (pack.custs.rows || []).slice(0, 10).map((c) => ({ name: c.name, revenue: c.revenue }));
  const arBuckets = Object.entries(pack.ar.buckets || {}).map(([k, v]) => ({ bucket: k, amount: v }));

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="mis-pack-page">
      <PageHeader eyebrow="MIS" title="MIS Pack"
        description="10-section management pack — EBITDA · Segments · Top products/customers · Compliance · Cash · Unusual · Downloadable Excel/PDF."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-40 rounded-sm h-9" data-testid="mis-from" />
            <span className="text-muted-foreground">→</span>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-40 rounded-sm h-9" data-testid="mis-to" />
            {departments.length > 0 && (
              <Select value={departmentId || "__all__"} onValueChange={(v) => setDepartmentId(v === "__all__" ? "" : v)}>
                <SelectTrigger className="rounded-sm h-9 w-48 gap-1" data-testid="mis-department">
                  <Building2 className="w-3.5 h-3.5" /><SelectValue placeholder="All departments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All departments</SelectItem>
                  {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.code} · {d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Button variant="outline" onClick={load} className="rounded-sm h-9" data-testid="mis-refresh">Refresh</Button>
            <Button variant="outline" onClick={downloadPdf} className="rounded-sm h-9 gap-1.5" data-testid="mis-download-pdf"><Download className="w-3.5 h-3.5" /> PDF</Button>
            <Button onClick={downloadXlsx} className="rounded-sm bg-accent text-white gap-1.5" data-testid="mis-download-xlsx"><Download className="w-3.5 h-3.5" /> Excel</Button>
          </div>
        } />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPI label="Revenue (YTD)" value={fmtINR(revYTD)} icon={IndianRupee} />
        <KPI label="EBITDA (Month)" value={fmtINR(ebitdaCurr)} tone={ebitdaCurr >= 0 ? "positive" : "negative"} icon={TrendingUp} />
        <KPI label="EBITDA (YTD)" value={fmtINR(ebitdaYTD)} tone="positive" icon={TrendingUp} />
        <KPI label="EAT (YTD)" value={fmtINR(eatYTD)} sub={`${(eatYTD / (revYTD || 1) * 100).toFixed(1)}% of revenue`} tone="positive" icon={TrendingUp} />
      </div>

      <Tabs defaultValue="pivot">
        <TabsList className="rounded-sm flex-wrap">
          <TabsTrigger value="pivot" className="rounded-sm bg-accent/10" data-testid="mis-tab-pivot">★ Pivot Builder</TabsTrigger>
          <TabsTrigger value="global" className="rounded-sm" data-testid="mis-tab-global">1 · Global EBITDA</TabsTrigger>
          <TabsTrigger value="segments" className="rounded-sm" data-testid="mis-tab-segments">2 · Segment EBITDA</TabsTrigger>
          <TabsTrigger value="products" className="rounded-sm" data-testid="mis-tab-products">3-4 · Products</TabsTrigger>
          <TabsTrigger value="customers" className="rounded-sm" data-testid="mis-tab-customers">5 · Top Customers</TabsTrigger>
          <TabsTrigger value="dashboards" className="rounded-sm" data-testid="mis-tab-dashboards">6 · Charts</TabsTrigger>
          <TabsTrigger value="unusual" className="rounded-sm" data-testid="mis-tab-unusual">7 · Unusual Txns</TabsTrigger>
          <TabsTrigger value="compliance" className="rounded-sm" data-testid="mis-tab-compliance">8 · Compliance</TabsTrigger>
          <TabsTrigger value="cash" className="rounded-sm" data-testid="mis-tab-cash">9 · Cash Position</TabsTrigger>
          <TabsTrigger value="profit" className="rounded-sm" data-testid="mis-tab-profit">10 · Profit vs Assets</TabsTrigger>
        </TabsList>

        <TabsContent value="pivot" className="mt-4">
          <PivotBuilder />
        </TabsContent>

        <TabsContent value="global" className="mt-4 space-y-3">
          <MisViewsBar dimension="global"
            rowOrder={globalRowOrder} columnOrder={globalColOrder}
            hiddenRows={globalHiddenRows} hiddenColumns={globalHiddenCols}
            onApplyView={(v) => {
              setGlobalRowOrder(v.row_order || []);
              setGlobalColOrder(v.column_order || []);
              setGlobalHiddenRows(new Set(v.hidden_rows || []));
              setGlobalHiddenCols(new Set(v.hidden_columns || []));
            }}
            onReset={() => {
              setGlobalRowOrder([]); setGlobalColOrder([]);
              setGlobalHiddenRows(new Set()); setGlobalHiddenCols(new Set());
            }} />
          <MisSortableTable
            rows={pack.global.rows}
            columns={pack.global.columns}
            rowOrder={globalRowOrder}
            columnOrder={globalColOrder}
            hiddenRows={globalHiddenRows}
            hiddenColumns={globalHiddenCols}
            onRowOrderChange={setGlobalRowOrder}
            onColumnOrderChange={setGlobalColOrder}
            onHideRow={(k) => setGlobalHiddenRows(new Set([...globalHiddenRows, k]))}
            onHideColumn={(id) => setGlobalHiddenCols(new Set([...globalHiddenCols, id]))}
            renderCell={(r, c) => (
              <span onClick={() => openDrill(r)} className="cursor-pointer">{fmtINR(r.values?.[c.id])}</span>
            )}
          />
          <div className="mt-6">
            <BudgetVarianceCard />
          </div>
        </TabsContent>

        <TabsContent value="segments" className="mt-4 space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <ApportionmentConfig dimension="business" />
            <ApportionmentConfig dimension="geo" />
          </div>

          {/* Business Segment MIS · drag-drop + saved views */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-mono uppercase text-muted-foreground">By Business Segment</div>
              <MisViewsBar dimension="business"
                rowOrder={bizRowOrder} columnOrder={bizColOrder}
                hiddenRows={bizHiddenRows} hiddenColumns={bizHiddenCols}
                onApplyView={(v) => {
                  setBizRowOrder(v.row_order || []); setBizColOrder(v.column_order || []);
                  setBizHiddenRows(new Set(v.hidden_rows || []));
                  setBizHiddenCols(new Set(v.hidden_columns || []));
                }}
                onReset={() => { setBizRowOrder([]); setBizColOrder([]); setBizHiddenRows(new Set()); setBizHiddenCols(new Set()); }} />
            </div>
            {bizFullPnl?.rows ? (
              <MisSortableTable
                rows={bizFullPnl.rows.map(r => ({ key: r.key, label: r.label, tone: r.tone, values: r.values }))}
                columns={bizFullPnl.columns.map(c => ({ id: c.id, label: c.name || c.label || c.code, code: c.code }))}
                rowOrder={bizRowOrder} columnOrder={bizColOrder}
                hiddenRows={bizHiddenRows} hiddenColumns={bizHiddenCols}
                onRowOrderChange={setBizRowOrder} onColumnOrderChange={setBizColOrder}
                onHideRow={(k) => setBizHiddenRows(new Set([...bizHiddenRows, k]))}
                onHideColumn={(id) => setBizHiddenCols(new Set([...bizHiddenCols, id]))}
                renderCell={(r, c) => fmtINR(r.values?.[c.id])}
              />
            ) : (
              <div className="italic text-xs text-muted-foreground p-4 border rounded-sm">
                Loading segment MIS… (make sure Business Segments are defined)
              </div>
            )}
            <ResponsiveContainer width="100%" height={220} className="mt-4">
              <BarChart data={segBizChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" fontSize={10} /><YAxis fontSize={10} tickFormatter={fmtINRCompact} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 12 }} formatter={(v) => fmtINR(v)} />
                <Bar dataKey="ebitda" fill="hsl(220 90% 55%)" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Geo Segment MIS · drag-drop + saved views */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-mono uppercase text-muted-foreground">By Geography</div>
              <MisViewsBar dimension="geo"
                rowOrder={geoRowOrder} columnOrder={geoColOrder}
                hiddenRows={geoHiddenRows} hiddenColumns={geoHiddenCols}
                onApplyView={(v) => {
                  setGeoRowOrder(v.row_order || []); setGeoColOrder(v.column_order || []);
                  setGeoHiddenRows(new Set(v.hidden_rows || []));
                  setGeoHiddenCols(new Set(v.hidden_columns || []));
                }}
                onReset={() => { setGeoRowOrder([]); setGeoColOrder([]); setGeoHiddenRows(new Set()); setGeoHiddenCols(new Set()); }} />
            </div>
            {geoFullPnl?.rows ? (
              <MisSortableTable
                rows={geoFullPnl.rows.map(r => ({ key: r.key, label: r.label, tone: r.tone, values: r.values }))}
                columns={geoFullPnl.columns.map(c => ({ id: c.id, label: c.name || c.label || c.code, code: c.code }))}
                rowOrder={geoRowOrder} columnOrder={geoColOrder}
                hiddenRows={geoHiddenRows} hiddenColumns={geoHiddenCols}
                onRowOrderChange={setGeoRowOrder} onColumnOrderChange={setGeoColOrder}
                onHideRow={(k) => setGeoHiddenRows(new Set([...geoHiddenRows, k]))}
                onHideColumn={(id) => setGeoHiddenCols(new Set([...geoHiddenCols, id]))}
                renderCell={(r, c) => fmtINR(r.values?.[c.id])}
              />
            ) : (
              <div className="italic text-xs text-muted-foreground p-4 border rounded-sm">
                Loading geo segment MIS…
              </div>
            )}
            <ResponsiveContainer width="100%" height={220} className="mt-4">
              <BarChart data={segGeoChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" fontSize={10} /><YAxis fontSize={10} tickFormatter={fmtINRCompact} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 12 }} formatter={(v) => fmtINR(v)} />
                <Bar dataKey="ebitda" fill="hsl(158 89% 33%)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </TabsContent>

        <TabsContent value="products" className="mt-4 space-y-4">
          <div className="text-xs font-mono uppercase text-muted-foreground">Top 10 Products — Gross margin ranked</div>
          <div className="card-sharp overflow-hidden">
            <table className="w-full dense">
              <thead><tr><th>#</th><th>Product</th><th className="text-right">Qty</th><th className="text-right">Revenue</th><th className="text-right">Cost</th><th className="text-right">GM</th><th className="text-right">Margin%</th></tr></thead>
              <tbody>
                {pack.prods.rows.map((r, i) => (
                  <tr key={r.item_id}><td className="text-xs text-muted-foreground">{i + 1}</td><td>{r.name}</td>
                    <td className="text-right tabular-nums text-xs">{fmtNum(r.qty_sold)}</td>
                    <td className="text-right tabular-nums">{fmtINR(r.revenue)}</td>
                    <td className="text-right tabular-nums text-xs">{fmtINR(r.cost)}</td>
                    <td className={`text-right tabular-nums font-medium ${r.gross_margin >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtINR(r.gross_margin)}</td>
                    <td className="text-right tabular-nums text-xs">{r.margin_pct.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="customers" className="mt-4 space-y-4">
          <div className="text-xs font-mono uppercase text-muted-foreground">Top 10 Customers — Revenue ranked</div>
          <div className="card-sharp overflow-hidden">
            <table className="w-full dense">
              <thead><tr><th>#</th><th>Customer</th><th>GSTIN</th><th>State</th><th className="text-right">Invoices</th><th className="text-right">Revenue</th><th className="text-right">Outstanding</th></tr></thead>
              <tbody>
                {pack.custs.rows.map((r, i) => (
                  <tr key={r.customer_id}><td className="text-xs text-muted-foreground">{i + 1}</td><td>{r.name}</td>
                    <td className="font-mono text-xs">{r.gstin || "—"}</td><td className="text-xs">{r.state || "—"}</td>
                    <td className="text-right tabular-nums text-xs">{r.invoices}</td>
                    <td className="text-right tabular-nums font-medium">{fmtINR(r.revenue)}</td>
                    <td className="text-right tabular-nums text-xs">{fmtINR(r.outstanding)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="dashboards" className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card-sharp p-5">
            <div className="text-xs font-mono uppercase text-muted-foreground mb-2">Top products — gross margin</div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={topProdChart} layout="vertical" margin={{ left: 100 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" fontSize={10} tickFormatter={fmtINRCompact} />
                <YAxis type="category" dataKey="name" fontSize={10} width={100} />
                <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                <Bar dataKey="gm" fill="hsl(158 89% 33%)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="card-sharp p-5">
            <div className="text-xs font-mono uppercase text-muted-foreground mb-2">Top customers — revenue</div>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={topCustChart} dataKey="revenue" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => e.name.slice(0, 12)}>
                  {topCustChart.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtINR(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="card-sharp p-5">
            <div className="text-xs font-mono uppercase text-muted-foreground mb-2">Segment EBITDA — Business</div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={segBizChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" fontSize={10} /><YAxis fontSize={10} tickFormatter={fmtINRCompact} />
                <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                <Bar dataKey="ebitda" fill="hsl(280 76% 55%)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="card-sharp p-5">
            <div className="text-xs font-mono uppercase text-muted-foreground mb-2">AR Aging distribution</div>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={arBuckets} dataKey="amount" nameKey="bucket" cx="50%" cy="50%" outerRadius={90} label={(e) => e.bucket}>
                  {arBuckets.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtINR(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </TabsContent>

        <TabsContent value="unusual" className="mt-4 space-y-4">
          <div className="flex items-center gap-3">
            <Label>Threshold ₹</Label>
            <Input type="number" value={threshold} onChange={(e) => setThreshold(Number(e.target.value))} className="w-40 rounded-sm h-9" data-testid="unusual-threshold" />
            <Button variant="outline" onClick={load} className="rounded-sm h-9" data-testid="unusual-apply">Apply</Button>
            <div className="text-xs text-muted-foreground">{unusual.count} vouchers ≥ {fmtINR(threshold)}</div>
          </div>
          <div className="card-sharp overflow-hidden">
            <table className="w-full dense">
              <thead><tr><th>Date</th><th>Voucher #</th><th>Type</th><th className="text-right">Amount</th><th>Narration</th></tr></thead>
              <tbody>
                {unusual.rows.length === 0 && <tr><td colSpan="5" className="text-center py-8 text-muted-foreground">No transactions above threshold in this period.</td></tr>}
                {unusual.rows.map((r) => (
                  <tr key={r.id}><td className="text-xs">{fmtDate(r.date)}</td><td className="font-mono text-xs">{r.voucher_number}</td>
                    <td className="text-xs text-muted-foreground">{r.voucher_type}</td>
                    <td className="text-right tabular-nums font-medium">{fmtINR(r.amount)}</td>
                    <td className="text-xs">{r.narration}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="compliance" className="mt-4 space-y-4">
          <div className="flex items-center gap-4 text-sm">
            <div><span className="text-muted-foreground">Overdue:</span> <span className="font-semibold text-red-600">{pack.comp.counts?.overdue || 0}</span></div>
            <div><span className="text-muted-foreground">Due in 30d:</span> <span className="font-semibold text-amber-600">{pack.comp.counts?.upcoming_30d || 0}</span></div>
          </div>
          <div className="card-sharp overflow-hidden">
            <table className="w-full dense">
              <thead><tr><th>#</th><th>Filing</th><th>Period</th><th>Due date</th><th>Status</th></tr></thead>
              <tbody>
                {(pack.comp.all_events || []).slice(0, 40).map((ev, i) => (
                  <tr key={i}><td className="text-xs text-muted-foreground">{i + 1}</td><td>{ev.kind}</td><td>{ev.period}</td>
                    <td className="text-xs">{fmtDate(ev.due_date)}</td>
                    <td><span className={`text-xs px-2 py-0.5 rounded-sm ${ev.status === "overdue" ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>{ev.status.toUpperCase()}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="cash" className="mt-4 space-y-4">
          <div className="text-xs font-mono uppercase text-muted-foreground">Open the Reports → Cash Flow tab for full 3-activity drill-down.</div>
          <div className="grid grid-cols-2 gap-4">
            <div className="card-sharp p-5">
              <div className="text-[10px] font-mono uppercase text-muted-foreground">AR Aging Total</div>
              <div className="metric-value text-2xl mt-2">{fmtINR(pack.ar.rows?.reduce((s, r) => s + r.amount, 0) || 0)}</div>
              <div className="text-xs text-muted-foreground mt-1">{pack.ar.rows?.length || 0} open invoices</div>
            </div>
            <div className="card-sharp p-5">
              <div className="text-[10px] font-mono uppercase text-muted-foreground">AP Aging Total</div>
              <div className="metric-value text-2xl mt-2">{fmtINR(pack.ap.rows?.reduce((s, r) => s + r.amount, 0) || 0)}</div>
              <div className="text-xs text-muted-foreground mt-1">{pack.ap.rows?.length || 0} open bills</div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="profit" className="mt-4 space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="card-sharp p-5">
              <div className="text-[10px] font-mono uppercase text-muted-foreground">Reporting-month Profit</div>
              <div className={`metric-value text-2xl mt-2 ${globRow("eat").reporting_month >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                {fmtINR(globRow("eat").reporting_month || 0)}
              </div>
            </div>
            <div className="card-sharp p-5">
              <div className="text-[10px] font-mono uppercase text-muted-foreground">Reporting-month EBITDA</div>
              <div className="metric-value text-2xl mt-2 text-emerald-600">{fmtINR(globRow("ebitda").reporting_month || 0)}</div>
            </div>
            <div className="card-sharp p-5">
              <div className="text-[10px] font-mono uppercase text-muted-foreground">Depreciation charged</div>
              <div className="metric-value text-2xl mt-2 text-red-600">{fmtINR(globRow("depreciation_amortization")?.reporting_month || 0)}</div>
              <div className="text-xs text-muted-foreground mt-1">Represents wear-and-tear on assets</div>
            </div>
          </div>
          <div className="card-sharp p-5">
            <div className="text-xs text-muted-foreground">
              Profit for the period reflects the earnings backed by capex assets on the balance sheet.
              Depreciation is the accounting recognition that the asset base is being consumed to generate this profit —
              use the <b>Fixed Assets → FAR</b> to see the Gross Block, Accumulated Depreciation, and Net Book Value that back this period's earnings.
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Iter-109/110 · P&L drill-down modal with By-Department + By-Account tabs */}
      <Dialog open={!!drillRow} onOpenChange={(o) => { if (!o) { setDrillRow(null); setDrillData(null); setAccData(null); } }}>
        <DialogContent className="rounded-sm sm:max-w-3xl" data-testid="mis-drill-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-accent" />
              {drillRow?.label} · drill-down
            </DialogTitle>
          </DialogHeader>
          {drillBusy ? (
            <div className="py-8 text-center text-muted-foreground text-sm">Loading drill-down…</div>
          ) : (
            <DTabs defaultValue="by-dept">
              <DTL className="rounded-sm">
                <DTT value="by-dept" className="rounded-sm" data-testid="drill-tab-dept">By Department</DTT>
                <DTT value="by-acc" className="rounded-sm" data-testid="drill-tab-acc">By Account</DTT>
                <DTT value="by-vch" className="rounded-sm" data-testid="drill-tab-vch">By Voucher</DTT>
              </DTL>

              <DTC value="by-dept" className="mt-4">
                {!drillData || drillData.rows.length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground text-sm">No transactions for this row.</div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="rounded-sm border p-2"><div className="text-[10px] font-mono uppercase text-muted-foreground">Total</div><div className="text-lg font-bold tabular-nums">{fmtINR(drillData.total_amount)}</div></div>
                      <div className="rounded-sm border p-2"><div className="text-[10px] font-mono uppercase text-muted-foreground">Departments</div><div className="text-lg font-bold tabular-nums">{drillData.rows.length}</div></div>
                      <div className="rounded-sm border p-2"><div className="text-[10px] font-mono uppercase text-muted-foreground">Entries</div><div className="text-lg font-bold tabular-nums">{drillData.rows.reduce((s, r) => s + r.entry_count, 0)}</div></div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div className="h-52">
                        <ResponsiveContainer width="100%" height="100%">
                          <PC>
                            <P2 data={drillData.rows} dataKey="amount" nameKey="name" outerRadius={70} label={(e) => `${e.code}`}>
                              {drillData.rows.map((_, i) => <C2 key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                            </P2>
                            <Tooltip formatter={(v) => fmtINR(v)} />
                          </PC>
                        </ResponsiveContainer>
                      </div>
                      <div className="overflow-auto max-h-52">
                        <table className="w-full dense text-xs">
                          <thead><tr><th>Department</th><th className="text-right">Amount</th><th className="text-right">%</th></tr></thead>
                          <tbody>
                            {drillData.rows.map((row) => (
                              <tr key={row.department_id || "u"} data-testid={`drill-row-${row.code}`}>
                                <td><span className="font-mono text-xs">{row.code}</span> · {row.name}</td>
                                <td className="text-right tabular-nums">{fmtINR(row.amount)}</td>
                                <td className="text-right tabular-nums text-muted-foreground">{row.share_pct}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </DTC>

              <DTC value="by-acc" className="mt-4">
                {!accData || accData.rows.length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground text-sm">No accounts contributed to this row.</div>
                ) : (
                  <div className="space-y-2">
                    <div className="text-[10px] font-mono uppercase text-muted-foreground">
                      // Total {fmtINR(accData.total_amount)} · {accData.rows.length} contributing accounts
                    </div>
                    <div className="overflow-auto max-h-96 rounded-sm border">
                      <table className="w-full dense text-xs">
                        <thead className="sticky top-0 bg-card">
                          <tr><th>#</th><th>Account</th><th className="text-right">Entries</th><th className="text-right">Amount</th><th className="text-right">%</th></tr>
                        </thead>
                        <tbody>
                          {accData.rows.map((row, i) => (
                            <tr key={row.account_id} data-testid={`drill-acc-${row.code}`}>
                              <td className="text-muted-foreground">{i + 1}</td>
                              <td><span className="font-mono">{row.code || "—"}</span> · {row.name}</td>
                              <td className="text-right tabular-nums text-muted-foreground">{row.entry_count}</td>
                              <td className="text-right tabular-nums font-medium">{fmtINR(row.amount)}</td>
                              <td className="text-right tabular-nums text-muted-foreground">{row.share_pct}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </DTC>

              <DTC value="by-vch" className="mt-4">
                {!vchData || vchData.rows.length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground text-sm">No vouchers found for this row.</div>
                ) : (
                  <div className="space-y-2">
                    <div className="text-[10px] font-mono uppercase text-muted-foreground">
                      // {vchData.rows.length} voucher{vchData.rows.length !== 1 ? "s" : ""} · total {fmtINR(vchData.total_amount)}
                      {vchData.truncated && <span className="text-amber-700 ml-1">· truncated to 200</span>}
                    </div>
                    <div className="overflow-auto max-h-96 rounded-sm border">
                      <table className="w-full dense text-xs">
                        <thead className="sticky top-0 bg-card">
                          <tr><th>#</th><th>Voucher</th><th>Date</th><th>Type</th><th>Narration</th><th className="text-right">Amount</th><th className="text-right">%</th></tr>
                        </thead>
                        <tbody>
                          {vchData.rows.map((row, i) => (
                            <tr key={row.voucher_id} data-testid={`drill-vch-${row.voucher_number}`}>
                              <td className="text-muted-foreground">{i + 1}</td>
                              <td><a href={`/app/vouchers?id=${row.voucher_id}`} className="font-mono text-blue-700 hover:underline">{row.voucher_number}</a></td>
                              <td className="text-xs">{row.date}</td>
                              <td className="text-[10px] font-mono uppercase text-muted-foreground">{row.voucher_type}</td>
                              <td className="text-xs max-w-[240px] truncate">{row.narration}</td>
                              <td className="text-right tabular-nums font-medium">{fmtINR(row.amount)}</td>
                              <td className="text-right tabular-nums text-muted-foreground">{row.share_pct}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </DTC>
            </DTabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
